/**
 * Fancybox lightbox for eligible images inside `.alteia-single-post__body`.
 *
 * Only image destinations are enhanced. Existing non-image links are left alone.
 */
import { Fancybox } from '@fancyapps/ui/dist/fancybox/';
import '@fancyapps/ui/dist/fancybox/fancybox.css';

const BODY_SELECTOR = '.alteia-single-post__body';
const LIGHTBOX_ATTR = 'data-fancybox';
const LIGHTBOX_LINK_CLASS = 'alteia-single-post__lightbox-link';
const IMAGE_EXT_RE = /\.(jpe?g|png|gif|webp|avif)(?:\?|#|$)/i;
const WP_SIZE_SUFFIX_RE =
	/-\d+x\d+(?=\.(?:jpe?g|png|gif|webp|avif)(?:\?|#|$))/i;

/**
 * Whether a URL points at a common raster image file.
 *
 * @param {string} url Candidate URL.
 * @return {boolean} True when the URL looks like an image.
 */
function isImageUrl( url ) {
	if ( ! url || typeof url !== 'string' ) {
		return false;
	}

	const trimmed = url.trim();

	if ( ! trimmed || trimmed.startsWith( '#' ) || trimmed.startsWith( 'mailto:' ) || trimmed.startsWith( 'tel:' ) ) {
		return false;
	}

	if ( /^data:image\//i.test( trimmed ) ) {
		return true;
	}

	try {
		const parsed = new URL( trimmed, window.location.href );
		return IMAGE_EXT_RE.test( parsed.pathname );
	} catch ( error ) {
		return IMAGE_EXT_RE.test( trimmed );
	}
}

/**
 * Prefer WordPress original upload URL when a sized intermediate is detected.
 *
 * @param {string} url Image URL.
 * @return {string} Possible original URL.
 */
function toPossibleOriginalUrl( url ) {
	if ( ! url ) {
		return url;
	}

	return url.replace( WP_SIZE_SUFFIX_RE, '' );
}

/**
 * Resolve the best full-size URL for an image element.
 *
 * @param {HTMLImageElement} img Image element.
 * @return {string} Full-size (or largest available) image URL.
 */
function getBestImageUrl( img ) {
	const srcset = img.getAttribute( 'srcset' );
	let bestUrl = '';
	let bestWidth = 0;

	if ( srcset ) {
		srcset.split( ',' ).forEach( ( entry ) => {
			const parts = entry.trim().split( /\s+/ );
			const url = parts[ 0 ];
			const descriptor = parts[ 1 ] || '';
			const width = descriptor.endsWith( 'w' )
				? parseInt( descriptor, 10 )
				: 0;

			if ( url && width >= bestWidth ) {
				bestWidth = width;
				bestUrl = url;
			}
		} );
	}

	const fallback =
		bestUrl || img.currentSrc || img.getAttribute( 'src' ) || '';

	return toPossibleOriginalUrl( fallback );
}

/**
 * Caption text from the nearest figure, if any.
 *
 * @param {HTMLImageElement} img Image element.
 * @return {string} Caption text.
 */
function getImageCaption( img ) {
	const figure = img.closest( 'figure' );
	const caption = figure?.querySelector( 'figcaption' );

	return caption?.textContent?.trim() || img.getAttribute( 'alt' ) || '';
}

/**
 * Whether an image should be skipped for lightbox treatment.
 *
 * @param {HTMLImageElement} img Image element.
 * @return {boolean} True when the image is ineligible.
 */
function shouldSkipImage( img ) {
	if ( img.dataset.alteiaLightbox === 'true' ) {
		return true;
	}

	if (
		img.getAttribute( 'aria-hidden' ) === 'true' ||
		img.getAttribute( 'role' ) === 'presentation' ||
		img.getAttribute( 'role' ) === 'none'
	) {
		return true;
	}

	const width = parseInt( img.getAttribute( 'width' ) || '0', 10 );
	const height = parseInt( img.getAttribute( 'height' ) || '0', 10 );

	if ( ( width > 0 && width <= 1 ) || ( height > 0 && height <= 1 ) ) {
		return true;
	}

	return false;
}

/**
 * Mark an anchor for Fancybox and apply the lightbox link class.
 *
 * @param {HTMLAnchorElement} link        Anchor element.
 * @param {string}            galleryName Fancybox gallery group name.
 * @param {HTMLImageElement}  img         Related image element.
 * @return {void}
 */
function enhanceImageLink( link, galleryName, img ) {
	link.setAttribute( LIGHTBOX_ATTR, galleryName );
	link.classList.add( LIGHTBOX_LINK_CLASS );

	const caption = getImageCaption( img );

	if ( caption && ! link.getAttribute( 'data-caption' ) ) {
		link.setAttribute( 'data-caption', caption );
	}

	img.dataset.alteiaLightbox = 'true';
}

/**
 * Wrap an unlinked image so Fancybox can open the full-size source.
 *
 * @param {HTMLImageElement} img         Image element.
 * @param {string}           galleryName Fancybox gallery group name.
 * @return {void}
 */
function wrapImageForLightbox( img, galleryName ) {
	const href = getBestImageUrl( img );

	if ( ! href || ! isImageUrl( href ) ) {
		return;
	}

	const parent = img.parentElement;

	if ( ! parent ) {
		return;
	}

	const link = img.ownerDocument.createElement( 'a' );
	link.href = href;
	enhanceImageLink( link, galleryName, img );

	parent.insertBefore( link, img );
	link.appendChild( img );
}

/**
 * Prepare eligible body images, then bind Fancybox once per article body.
 *
 * @param {HTMLElement} root Single post root element.
 * @return {void}
 */
export function initPostLightbox( root ) {
	if ( ! root || root.dataset.lightboxBound === 'true' ) {
		return;
	}

	const body = root.querySelector( BODY_SELECTOR );

	if ( ! body || typeof Fancybox?.bind !== 'function' ) {
		return;
	}

	root.dataset.lightboxBound = 'true';

	const postId =
		root
			.querySelector( '[data-post-id]' )
			?.getAttribute( 'data-post-id' ) ||
		root.getAttribute( 'data-post-id' ) ||
		'post';
	const galleryName = `alteia-post-${ postId }`;

	body.querySelectorAll( 'img' ).forEach( ( img ) => {
		if ( shouldSkipImage( img ) ) {
			return;
		}

		const parentLink = img.closest( 'a' );

		if ( parentLink && body.contains( parentLink ) ) {
			const href = parentLink.getAttribute( 'href' ) || '';

			// Preserve intentional non-image destinations (pages, PDFs, etc.).
			if ( ! isImageUrl( href ) ) {
				return;
			}

			enhanceImageLink( parentLink, galleryName, img );
			return;
		}

		wrapImageForLightbox( img, galleryName );
	} );

	Fancybox.bind( body, `[${ LIGHTBOX_ATTR }]`, {
		groupAll: false,
		Carousel: {
			Toolbar: {
				display: {
					left: [ 'counter' ],
					middle: [],
					right: [ 'close' ],
				},
			},
			Thumbs: false,
		},
	} );
}
